import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LineChart,
  BarChart3,
  Brain,
  Target,
  TrendingUp,
  CheckCircle,
  ArrowRight,
  Search,
  Settings,
  BarChart2,
  Lightbulb,
  ChevronRight,
  MapPin,
  Clock,
  Mail,
  Zap,
  Eye,
  Building,
  ShoppingCart,
  Briefcase,
  PieChart,
  Monitor,
  HelpCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import CookieBanner from "@/components/CookieBanner";

const WHATSAPP_URL = "https://wa.me/34672972514?text=Hola%2C%20me%20interesa%20el%20servicio%20de%20Business%20Intelligence%20para%20mi%20empresa";
const LOGO_URL = "/datyon-logo.png";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const FAQS = [
  {
    q: "¿Qué es exactamente Business Intelligence aplicado a una empresa de Murcia?",
    a: "Business Intelligence es el conjunto de herramientas y procesos que convierten los datos dispersos de tu empresa en cuadros de mando útiles. Aplicado en Murcia, suele empezar conectando ERP, facturación y ventas para que dirección vea cómo va el negocio en tiempo real."
  },
  {
    q: "¿Necesita una PYME de Murcia una solución de Business Intelligence o es solo para grandes empresas?",
    a: "No es una cuestión de tamaño sino de control. Si tomas decisiones que mueven dinero —compras, precios, contrataciones— una solución de Business Intelligence en Murcia te ahorra meses de prueba y error y suele amortizarse en pocos meses."
  },
  {
    q: "¿Cuánto tarda en implementarse un proyecto de Business Intelligence?",
    a: "Un proyecto típico de Business Intelligence tarda entre 4 y 8 semanas en estar productivo, según el número de fuentes y la complejidad. Entrego incrementos cada pocas semanas para que veas valor desde el principio, no al final."
  },
  {
    q: "¿Qué herramientas usáis para los proyectos de Business Intelligence en Murcia?",
    a: "Trabajo principalmente con Power BI por su madurez, integración con Microsoft 365 y coste razonable para PYMEs. Cuando es necesario, completo con SQL para modelado de datos y Python para automatizaciones e integraciones específicas."
  },
  {
    q: "¿Qué retorno real puedo esperar de un proyecto de Business Intelligence?",
    a: "Las empresas con las que he trabajado suelen recuperar la inversión detectando fugas concretas: stock parado, clientes no rentables o partidas de coste descontroladas. El retorno depende del caso, pero rara vez es pequeño."
  },
  {
    q: "¿Puedo ampliar la solución de Business Intelligence cuando mi negocio crezca?",
    a: "Sí. La solución se diseña en capas para que puedas añadir nuevas fuentes, KPIs o dashboards sin rehacer lo anterior. Es habitual empezar con un cuadro de mando y crecer desde ahí cuando la empresa lo demanda."
  }
];

export default function BusinessIntelligenceMurcia() {
  useEffect(() => {
    document.title = "Business Intelligence en Murcia | Dashboards y análisis | Datyon";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", "Implementa soluciones de Business Intelligence en Murcia. Visualiza tus datos y toma decisiones estratégicas en tiempo real.");
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute("href", "https://datyon.com/business-intelligence-murcia");
    }

    // FAQPage JSON-LD para rich snippets en Google
    const ldScript = document.createElement("script");
    ldScript.type = "application/ld+json";
    ldScript.id = "bi-faq-jsonld";
    ldScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQS.map((f) => ({
        "@type": "Question",
        "name": f.q,
        "acceptedAnswer": { "@type": "Answer", "text": f.a }
      }))
    });
    document.head.appendChild(ldScript);

    window.scrollTo(0, 0);
    return () => {
      document.title = "Análisis de Datos en Murcia para Empresas | Datyon";
      if (metaDesc) metaDesc.setAttribute("content", "Transforma los datos de tu empresa en decisiones rentables. Servicios de análisis de datos, business intelligence y modelos predictivos en Murcia.");
      if (canonical) canonical.setAttribute("href", "https://datyon.com/");
      const existing = document.getElementById("bi-faq-jsonld");
      if (existing) existing.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#020617] relative overflow-x-hidden">
      <div className="noise-overlay" />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass" data-testid="bi-navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center pl-4" data-testid="bi-nav-logo">
              <img src={LOGO_URL} alt="DATYON - Business Intelligence en Murcia" className="h-20 w-auto" style={{ maxHeight: '80px' }} />
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Inicio</Link>
              <Link to="/servicios" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Servicios</Link>
              <Link to="/proceso" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Proceso</Link>
              <Link to="/contacto" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Contacto</Link>
            </div>
          </div>
        </div>
      </nav>

      <main>
        {/* Hero */}
        <section className="relative min-h-[80vh] flex items-center section-padding pt-24 overflow-hidden" data-testid="bi-hero">
          <div className="spotlight-1 -top-40 -left-40" />
          <div className="spotlight-2 top-1/3 right-0" />
          <div className="max-w-7xl mx-auto w-full relative z-10">
            <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-3xl">
              <motion.div variants={fadeInUp} className="flex items-center gap-2 mb-4">
                <MapPin className="w-4 h-4 text-indigo-400" />
                <p className="text-indigo-400 font-medium text-sm md:text-base">
                  DATYON — Murcia, España
                </p>
              </motion.div>
              <motion.h1 variants={fadeInUp} className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-6">
                Business Intelligence en{" "}
                <span className="text-gradient">Murcia</span>
              </motion.h1>
              <motion.p variants={fadeInUp} className="text-slate-400 text-base md:text-lg mb-8 max-w-2xl">
                Diseñamos dashboards interactivos y sistemas de Business Intelligence para que las empresas 
                en Murcia tengan control total de su negocio en tiempo real. Visualiza ventas, rentabilidad, 
                KPIs y tendencias en un solo panel.
              </motion.p>
              <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4">
                <Button asChild className="btn-glow bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-6 text-base rounded-full" data-testid="bi-hero-cta">
                  <Link to="/contacto">
                    Solicita tu diagnóstico gratuito
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-base rounded-full transition-all hover:scale-105 hover:shadow-lg hover:shadow-green-600/30" data-testid="bi-hero-whatsapp">
                  <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    Contactar por WhatsApp
                  </a>
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Qué es el BI */}
        <section className="relative section-padding bg-slate-900/50" data-testid="bi-what-is">
          <div className="max-w-7xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}>
              <motion.div variants={fadeInUp} className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                  Qué es el <span className="text-gradient">Business Intelligence</span>
                </h2>
                <p className="text-slate-400 max-w-3xl mx-auto text-base md:text-lg leading-relaxed">
                  El Business Intelligence transforma los datos de tu empresa en dashboards visuales 
                  e interactivos. En lugar de revisar hojas de cálculo, tienes un panel donde ves 
                  en tiempo real las ventas, la rentabilidad, los KPIs financieros y el rendimiento 
                  de cada área de tu negocio.
                </p>
              </motion.div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  { icon: <LineChart className="w-7 h-7" />, title: "Ventas por cliente", desc: "Identifica qué clientes generan más ingresos y cuáles consumen más recursos sin rentabilidad proporcional." },
                  { icon: <PieChart className="w-7 h-7" />, title: "Rentabilidad por producto", desc: "Conoce el margen real de cada producto o servicio, descontando todos los costes asociados." },
                  { icon: <TrendingUp className="w-7 h-7" />, title: "Evolución mensual", desc: "Visualiza las tendencias de tu negocio mes a mes para anticipar problemas y detectar oportunidades." },
                  { icon: <Target className="w-7 h-7" />, title: "KPIs financieros", desc: "Los indicadores clave de tu negocio actualizados en tiempo real, accesibles desde cualquier dispositivo." },
                  { icon: <BarChart3 className="w-7 h-7" />, title: "Rendimiento de marketing", desc: "Mide el retorno real de cada campaña y canal de captación para invertir donde realmente funciona." },
                  { icon: <Monitor className="w-7 h-7" />, title: "Panel centralizado", desc: "Toda la información de tu empresa en un solo lugar, con visualizaciones claras y filtros interactivos." }
                ].map((item, i) => (
                  <motion.div key={i} variants={fadeInUp}>
                    <Card className="h-full bg-slate-900 border-slate-800 card-hover" data-testid={`bi-feature-${i}`}>
                      <CardContent className="p-6">
                        <div className="w-12 h-12 rounded-xl bg-indigo-600/20 flex items-center justify-center text-indigo-400 mb-4">
                          {item.icon}
                        </div>
                        <h3 className="font-semibold text-white mb-2">{item.title}</h3>
                        <p className="text-slate-400 text-sm">{item.desc}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Beneficios para empresas en Murcia */}
        <section className="relative section-padding overflow-hidden" data-testid="bi-benefits">
          <div className="spotlight-1 top-0 right-0" />
          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}>
              <motion.div variants={fadeInUp} className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                  Beneficios del BI para empresas en Murcia
                </h2>
                <p className="text-slate-400 max-w-2xl mx-auto">
                  Trabajamos desde Murcia con empresas de la Región y de toda España, adaptando cada proyecto a las necesidades reales del negocio.
                </p>
              </motion.div>

              <div className="grid md:grid-cols-2 gap-8">
                <motion.div variants={fadeInUp}>
                  <div className="glass-card rounded-2xl p-8">
                    <h3 className="text-red-400 font-semibold mb-6 flex items-center gap-2">
                      <Eye className="w-5 h-5" />
                      Sin Business Intelligence
                    </h3>
                    <ul className="space-y-4">
                      {[
                        "Revisas datos en hojas de cálculo dispersas",
                        "No sabes qué productos son realmente rentables",
                        "Los informes llegan tarde y desactualizados",
                        "Tomas decisiones sin una visión global del negocio"
                      ].map((item, i) => (
                        <li key={i} className="flex items-start gap-3 text-slate-300">
                          <Target className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>

                <motion.div variants={fadeInUp}>
                  <div className="glass-card rounded-2xl p-8">
                    <h3 className="text-cyan-400 font-semibold mb-6 flex items-center gap-2">
                      <Zap className="w-5 h-5" />
                      Con Business Intelligence
                    </h3>
                    <ul className="space-y-4">
                      {[
                        "Panel visual con toda la información en tiempo real",
                        "Conoces la rentabilidad de cada producto y cliente",
                        "Informes automatizados y siempre actualizados",
                        "Visión completa del negocio para decidir con datos"
                      ].map((item, i) => (
                        <li key={i} className="flex items-start gap-3 text-slate-300">
                          <CheckCircle className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Casos de uso */}
        <section className="relative section-padding bg-slate-900/50" data-testid="bi-use-cases">
          <div className="max-w-7xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}>
              <motion.div variants={fadeInUp} className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                  BI aplicado a empresas en <span className="text-gradient">Murcia</span>
                </h2>
                <p className="text-slate-400 max-w-2xl mx-auto">
                  Adaptamos cada proyecto de Business Intelligence a las necesidades concretas de tu sector y tu empresa.
                </p>
              </motion.div>

              <div className="grid md:grid-cols-2 gap-6">
                {[
                  {
                    icon: <Briefcase className="w-6 h-6" />,
                    title: "Empresas de servicios",
                    items: ["Dashboard de rentabilidad por proyecto", "Control de horas y productividad", "Previsión de ingresos mensuales", "Análisis de clientes más rentables"]
                  },
                  {
                    icon: <ShoppingCart className="w-6 h-6" />,
                    title: "Ecommerce y retail",
                    items: ["Panel de ventas en tiempo real", "Análisis de ticket medio y conversión", "Seguimiento de inventario", "Rendimiento de campañas por canal"]
                  },
                  {
                    icon: <Building className="w-6 h-6" />,
                    title: "PYMES y negocios locales",
                    items: ["Visualización de ingresos y gastos", "Comparativa mensual y anual", "KPIs adaptados al negocio", "Alertas automáticas de desviaciones"]
                  },
                  {
                    icon: <TrendingUp className="w-6 h-6" />,
                    title: "Departamentos de marketing",
                    items: ["Atribución de conversiones por canal", "ROI de cada campaña publicitaria", "Segmentación de audiencias", "Reporting automatizado"]
                  }
                ].map((sector, i) => (
                  <motion.div key={i} variants={fadeInUp}>
                    <Card className="h-full bg-slate-900 border-slate-800 card-hover" data-testid={`bi-usecase-${i}`}>
                      <CardContent className="p-6 md:p-8">
                        <div className="flex items-center gap-4 mb-6">
                          <div className="w-12 h-12 rounded-xl bg-indigo-600/20 flex items-center justify-center text-indigo-400">
                            {sector.icon}
                          </div>
                          <h3 className="text-xl font-bold text-white">{sector.title}</h3>
                        </div>
                        <ul className="space-y-3">
                          {sector.items.map((item, j) => (
                            <li key={j} className="flex items-start gap-2 text-sm text-slate-300">
                              <CheckCircle className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Proceso */}
        <section className="relative section-padding overflow-hidden" data-testid="bi-process">
          <div className="spotlight-2 bottom-0 left-0" />
          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}>
              <motion.div variants={fadeInUp} className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                  Cómo implementamos BI en tu empresa
                </h2>
                <p className="text-slate-400 max-w-2xl mx-auto">
                  Un proceso claro y estructurado para que tu empresa empiece a tomar decisiones con datos reales.
                </p>
              </motion.div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { num: "01", icon: <Search className="w-6 h-6" />, title: "Diagnóstico gratuito", desc: "Analizamos qué datos tiene tu empresa, qué herramientas usas y qué información necesitas visualizar." },
                  { num: "02", icon: <BarChart2 className="w-6 h-6" />, title: "Diseño del dashboard", desc: "Definimos los KPIs clave y diseñamos la estructura del panel adaptada a tu negocio." },
                  { num: "03", icon: <Settings className="w-6 h-6" />, title: "Implementación", desc: "Conectamos las fuentes de datos, creamos las visualizaciones y configuramos automatizaciones." },
                  { num: "04", icon: <Lightbulb className="w-6 h-6" />, title: "Formación y entrega", desc: "Te enseñamos a usar el dashboard y te acompañamos en la adopción dentro de tu empresa." }
                ].map((step, i) => (
                  <motion.div key={i} variants={fadeInUp}>
                    <Card className="h-full bg-slate-900 border-slate-800 card-hover relative overflow-hidden">
                      <CardContent className="p-6">
                        <span className="absolute top-4 right-4 text-5xl font-bold text-slate-800/50">{step.num}</span>
                        <div className="w-12 h-12 rounded-xl bg-indigo-600/20 flex items-center justify-center text-indigo-400 mb-4">
                          {step.icon}
                        </div>
                        <h3 className="font-semibold text-white mb-2">{step.title}</h3>
                        <p className="text-slate-400 text-sm">{step.desc}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Herramientas */}
        <section className="relative section-padding bg-slate-900/50" data-testid="bi-tools">
          <div className="max-w-7xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}>
              <motion.div variants={fadeInUp} className="text-center mb-8">
                <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                  Herramientas de Business Intelligence
                </h2>
                <p className="text-slate-400 max-w-2xl mx-auto">
                  Trabajamos con las herramientas líderes del mercado para crear dashboards profesionales y escalables.
                </p>
              </motion.div>
              <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-4">
                {["Power BI", "Looker Studio", "Tableau", "Python", "SQL"].map((tool, i) => (
                  <div key={i} className="px-6 py-3 rounded-full bg-slate-800 border border-slate-700 text-white font-medium">
                    {tool}
                  </div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Resultados reales */}
        <section className="relative section-padding" data-testid="bi-results">
          <div className="max-w-7xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}>
              <motion.div variants={fadeInUp} className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                  Resultados del Business Intelligence
                </h2>
              </motion.div>

              <div className="grid md:grid-cols-3 gap-6">
                {[
                  { metric: "+25%", label: "rentabilidad anual", desc: "Empresa de servicios que detectó clientes no rentables gracias a un dashboard de horas y costes por proyecto." },
                  { metric: "+18%", label: "beneficio neto", desc: "Ecommerce que ajustó precios y promociones tras analizar márgenes reales por producto en su panel de BI." },
                  { metric: "-32%", label: "stock inmovilizado", desc: "Retail que optimizó inventario con un modelo predictivo de demanda integrado en su dashboard." }
                ].map((r, i) => (
                  <motion.div key={i} variants={fadeInUp}>
                    <Card className="h-full success-card border-slate-800 card-hover" data-testid={`bi-result-${i}`}>
                      <CardContent className="p-6 md:p-8 text-center">
                        <p className="text-4xl font-bold text-gradient mb-2">{r.metric}</p>
                        <p className="text-cyan-400 font-medium text-sm mb-4">{r.label}</p>
                        <p className="text-slate-400 text-sm">{r.desc}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Enlaces internos */}
        <section className="relative section-padding bg-slate-900/50" data-testid="bi-related">
          <div className="max-w-4xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}>
              <motion.div variants={fadeInUp} className="text-center mb-8">
                <h2 className="text-2xl sm:text-3xl font-bold mb-4">
                  Servicios relacionados
                </h2>
              </motion.div>
              <div className="grid sm:grid-cols-2 gap-4">
                <motion.div variants={fadeInUp}>
                  <Link to="/analisis-datos-empresas" className="block">
                    <Card className="bg-slate-900 border-slate-800 card-hover">
                      <CardContent className="p-6 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-indigo-600/20 flex items-center justify-center text-indigo-400 flex-shrink-0">
                          <BarChart3 className="w-6 h-6" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-white mb-1">Análisis de datos para empresas</h3>
                          <p className="text-slate-400 text-sm">Detectamos patrones y oportunidades en la información de tu negocio.</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-500 flex-shrink-0" />
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
                <motion.div variants={fadeInUp}>
                  <Link to="/servicios" className="block">
                    <Card className="bg-slate-900 border-slate-800 card-hover">
                      <CardContent className="p-6 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-indigo-600/20 flex items-center justify-center text-indigo-400 flex-shrink-0">
                          <Brain className="w-6 h-6" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-white mb-1">Todos nuestros servicios</h3>
                          <p className="text-slate-400 text-sm">Data Analytics, BI, Machine Learning y Automatización con IA.</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-500 flex-shrink-0" />
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* SEO Content Block */}
        <section className="relative section-padding" data-testid="bi-seo-content-block">
          <div className="max-w-4xl mx-auto">
            <div className="text-slate-400 text-base leading-relaxed space-y-6">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6">Qué es Business Intelligence y cómo puede ayudar a tu empresa en Murcia</h2>

              <p>
                Business Intelligence es, en esencia, la capacidad de convertir los datos de tu empresa en información útil para tomar decisiones. No es una herramienta concreta ni un software específico. Es un enfoque: usar lo que ya sabes de tu negocio — ventas, costes, clientes, operaciones — para ver con claridad qué funciona, qué no y dónde están las oportunidades de mejora.
              </p>

              <h3 className="text-xl font-bold text-white mt-8 mb-4">Para quién es el Business Intelligence</h3>

              <p>
                Cualquier empresa que tenga datos y tome decisiones puede beneficiarse del Business Intelligence. Pero donde más impacto tiene es en empresas medianas de sectores como industria, distribución, agroalimentaria y logística, donde el volumen de información es grande pero la capacidad de análisis suele ser limitada. Si tu empresa factura, tiene clientes, gestiona stock o lleva campañas comerciales, tienes datos suficientes para empezar a trabajar con BI. No necesitas ser una gran corporación ni tener un departamento de tecnología.
              </p>

              <h3 className="text-xl font-bold text-white mt-8 mb-4">Qué problemas resuelve el BI</h3>

              <p>
                El principal problema que resuelve es la falta de visibilidad. Muchas empresas no saben con exactitud qué productos les dejan margen, qué clientes son rentables de verdad, cómo evoluciona su negocio mes a mes o dónde se están yendo los costes. Con un buen sistema de Business Intelligence puedes tener esa información siempre actualizada, clara y accesible, sin depender de que alguien cruce datos en Excel o prepare un informe a mano.
              </p>

              <h3 className="text-xl font-bold text-white mt-8 mb-4">Ejemplos concretos de BI en acción</h3>

              <p>
                He implementado soluciones de Business Intelligence en empresas donde el director general necesitaba ver las ventas por zona y producto en tiempo real. En distribuidoras donde el responsable de compras necesitaba saber qué referencias pedir y cuáles parar. En industrias donde los jefes de planta querían medir el rendimiento por turno. En todos los casos, la clave no fue la herramienta, sino entender qué necesitaba ver cada persona para tomar mejores decisiones.
              </p>

              <h3 className="text-xl font-bold text-white mt-8 mb-4">Beneficios reales del Business Intelligence</h3>

              <p>
                El beneficio más inmediato es dejar de tomar decisiones con información incompleta o desactualizada. Pero a medio plazo, el BI permite detectar tendencias antes de que sea tarde, identificar problemas que no se ven a simple vista y reaccionar más rápido a los cambios del mercado. Si tu empresa está en Murcia o en cualquier punto de España y quieres empezar a usar tus datos de verdad, puedes consultar nuestra página de <Link to="/analisis-datos-empresas" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">análisis de datos para empresas</Link> o leer nuestro <Link to="/blog/como-saber-si-tu-empresa-pierde-dinero" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">artículo sobre cómo detectar pérdidas ocultas</Link>.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="relative section-padding overflow-hidden" data-testid="bi-faq">
          <div className="max-w-3xl mx-auto relative z-10">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}>
              <motion.div variants={fadeInUp} className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold mb-6" data-testid="bi-h2-faq">
                  Preguntas frecuentes sobre <span className="text-gradient">Business Intelligence en Murcia</span>
                </h2>
                <p className="text-slate-400 text-base leading-relaxed">
                  Resolvemos las dudas más habituales antes de empezar un proyecto de Business Intelligence.
                </p>
              </motion.div>

              <div className="space-y-4">
                {FAQS.map((faq, i) => (
                  <motion.div key={i} variants={fadeInUp}>
                    <Card className="bg-slate-900 border-slate-800" data-testid={`bi-faq-${i}`}>
                      <CardContent className="p-6">
                        <h3 className="font-semibold text-white mb-3 flex items-start gap-3">
                          <HelpCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                          {faq.q}
                        </h3>
                        <p className="text-slate-400 text-sm leading-relaxed pl-8">{faq.a}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA final */}
        <section className="relative section-padding" data-testid="bi-final-cta">
          <div className="max-w-4xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}>
              <motion.div variants={fadeInUp} className="glass-card rounded-2xl p-8 md:p-12 text-center">
                <h2 className="text-2xl sm:text-3xl font-bold mb-4">
                  Implementa Business Intelligence en tu empresa
                </h2>
                <p className="text-slate-400 mb-6 max-w-2xl mx-auto">
                  En el diagnóstico inicial gratuito analizamos qué datos tiene tu empresa, 
                  qué información necesitas visualizar y cómo podemos ayudarte. 
                  Después te proponemos una solución adaptada a tu negocio.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
                  <Button asChild className="btn-glow bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-6 rounded-full" data-testid="bi-cta-contact">
                    <Link to="/contacto">
                      Solicita tu diagnóstico gratuito
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                  </Button>
                  <Button asChild className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 rounded-full transition-all hover:scale-105" data-testid="bi-cta-whatsapp">
                    <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                      Escríbenos por WhatsApp
                    </a>
                  </Button>
                </div>

                <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-slate-400 text-sm">
                  <span className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-indigo-400" />
                    Respuesta en menos de 24 horas
                  </span>
                  <span className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-indigo-400" />
                    <a href="mailto:info@datyon.com" className="hover:text-white transition-colors">info@datyon.com</a>
                  </span>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-slate-800" data-testid="bi-footer">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="DATYON" className="h-8 w-auto" />
              <div className="flex items-center gap-2 text-slate-400">
                <MapPin className="w-4 h-4 text-indigo-400" />
                <span className="text-sm">Murcia, España</span>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/" className="text-slate-500 hover:text-white text-sm transition-colors">Inicio</Link>
              <Link to="/servicios" className="text-slate-500 hover:text-white text-sm transition-colors">Servicios</Link>
              <Link to="/contacto" className="text-slate-500 hover:text-white text-sm transition-colors">Contacto</Link>
            </div>
          </div>
          <div className="flex flex-wrap justify-center md:justify-start gap-4 mb-6 pt-4 border-t border-slate-800">
            <Link to="/aviso-legal" className="text-slate-500 hover:text-white text-sm transition-colors">Aviso Legal</Link>
            <span className="text-slate-700">|</span>
            <Link to="/politica-privacidad" className="text-slate-500 hover:text-white text-sm transition-colors">Política de Privacidad</Link>
            <span className="text-slate-700">|</span>
            <Link to="/politica-cookies" className="text-slate-500 hover:text-white text-sm transition-colors">Política de Cookies</Link>
          </div>
          <p className="text-slate-500 text-sm text-center md:text-left">
            © {new Date().getFullYear()} DATYON - Antonio Oliver. Todos los derechos reservados.
          </p>
        </div>
      </footer>

      {/* WhatsApp Floating */}
      <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-lg shadow-green-500/30 transition-all duration-300 hover:scale-110 group"
        data-testid="bi-whatsapp-float" aria-label="Contactar por WhatsApp">
        <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
        <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-slate-800 text-white text-sm px-3 py-2 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
          ¿Hablamos?
        </span>
      </a>

      <CookieBanner />
    </div>
  );
}
